import React, { useState } from 'react';
import { UserPlus, Send, Users, FileCheck, Mail, User, Phone, Upload, CheckCircle, AlertCircle } from 'lucide-react';

const TrabalheConosco = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: '',
  });
  const [arquivo, setArquivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verificar tamanho (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 10MB');
        return;
      }
      setArquivo(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nome', formData.nome);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telefone', formData.telefone);
      formDataToSend.append('mensagem', formData.mensagem);
      if (arquivo) {
        formDataToSend.append('arquivo', arquivo);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/curriculo`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
        setArquivo(null);
        // Limpar input de arquivo
        const fileInput = document.getElementById('arquivo-input');
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erro ao enviar currículo:', error);
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      number: '1',
      icon: Users,
      title: 'Conheça a Cultivar Agrícola',
      description: 'Entenda nossa história, valores e onde atuamos. Estamos em Vilhena-RO (matriz) e em mais 5 filiais',
    },
    {
      number: '2',
      icon: FileCheck,
      title: 'Acompanhe nossas oportunidades',
      description: 'Fique de olho nas nossas redes sociais e no site para conferir as vagas disponíveis',
    },
    {
      number: '3',
      icon: Send,
      title: 'Envie seu currículo',
      description: 'Encaminhe seu currículo diretamente para o nosso e-mail do RH ou entre em contato pelos canais oficiais',
    },
    {
      number: '4',
      icon: UserPlus,
      title: 'Participe do processo seletivo',
      description: 'Nossa equipe entrará em contato para entrevistas e demais etapas de seleção.',
    },
    {
      number: '5',
      icon: Users,
      title: 'Faça parte da Cultivar Agrícola',
      description: 'Se aprovado, você começa sua trajetória conosco, em um time que cresce junto com o agro.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6">COMO TRABALHAR CONOSCO</h1>
          <p className="text-lg md:text-2xl max-w-3xl mx-auto">
            Venha fazer parte da equipe que está cultivando bons negócios há 12 anos!
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 md:space-y-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-6 md:gap-8 ${
                    isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white shadow-2xl">
                      <span className="text-4xl md:text-6xl font-bold">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-gray-50 rounded-xl p-6 md:p-8 shadow-lg">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-2 md:p-3 rounded-full">
                        <Icon className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-800 ml-4">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formulário de Currículo */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-6 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <Mail className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">ENVIE SEU CURRÍCULO</h2>
              <p className="text-gray-600">
                Preencha o formulário abaixo e envie seu currículo para nosso RH
              </p>
              <p className="text-green-600 font-semibold mt-2">
                rh@cultivaragricola.com
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  <User className="w-5 h-5 inline mr-2" />
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                  placeholder="Seu nome completo"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <Mail className="w-5 h-5 inline mr-2" />
                    E-mail
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <Phone className="w-5 h-5 inline mr-2" />
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Mensagem / Área de Interesse
                </label>
                <textarea
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                  placeholder="Conte um pouco sobre você e em qual área gostaria de trabalhar..."
                ></textarea>
              </div>

              {/* Upload de Currículo */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  <Upload className="w-5 h-5 inline mr-2" />
                  Anexar Currículo (PDF, DOC, DOCX)
                </label>
                <div className="relative">
                  <input
                    id="arquivo-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="arquivo-input"
                    className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all"
                  >
                    <div className="text-center">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      {arquivo ? (
                        <p className="text-green-600 font-semibold">{arquivo.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-600">Clique para selecionar ou arraste o arquivo</p>
                          <p className="text-gray-400 text-sm mt-1">Máximo 10MB</p>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Status de envio */}
              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 rounded-lg p-4 flex items-center">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  <div>
                    <p className="text-green-800 font-semibold">Currículo enviado com sucesso!</p>
                    <p className="text-green-700 text-sm">Nossa equipe de RH entrará em contato em breve.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 rounded-lg p-4 flex items-center">
                  <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                  <div>
                    <p className="text-red-800 font-semibold">Erro ao enviar currículo</p>
                    <p className="text-red-700 text-sm">Por favor, tente novamente ou entre em contato pelo email rh@cultivaragricola.com</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full ${loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg flex items-center justify-center`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    ENVIANDO...
                  </>
                ) : (
                  <>
                    <Send className="w-6 h-6 mr-3" />
                    ENVIAR CURRÍCULO
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-800 mb-8 md:mb-12">Por que trabalhar na Cultivar?</h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg text-center">
              <div className="text-4xl md:text-5xl mb-4">🌱</div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">Crescimento Profissional</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Oportunidades de desenvolvimento em uma empresa em constante expansão
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg text-center">
              <div className="text-4xl md:text-5xl mb-4">🤝</div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">Ambiente Colaborativo</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Trabalhe em um time unido que valoriza parceria e dinamismo
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg text-center">
              <div className="text-4xl md:text-5xl mb-4">🎯</div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">Propósito</h3>
              <p className="text-gray-600 text-sm md:text-base">
                Faça parte de uma empresa que transforma o agronegócio regional
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrabalheConosco;